import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { blogPosts } from '../data/blogData';
import { 
  ArrowLeft, 
  Clock, 
  User, 
  Share2, 
  Heart, 
  BookOpen,
  MapPin,
  Phone
} from 'lucide-react';

const BlogPost = () => {
  const { slug } = useParams();
  
  // Find the blog post by slug
  const blogPost = blogPosts.find(post => post.slug === slug);
  
  // If no blog post found, show default content
  if (!blogPost) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-6">The article you're looking for doesn't exist.</p>
          <Link to="/blogs">
            <Button className="bg-orange-600 hover:bg-orange-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link to="/blogs">
          <Button variant="outline" className="mb-6 border-orange-200 text-orange-600">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
        </Link>

        {/* Article Header */}
        <Card className="mb-8 border-orange-100">
          <CardContent className="p-8">
            <Badge className="bg-orange-100 text-orange-700 mb-4">
              {blogPost.category}
            </Badge>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {blogPost.title}
            </h1>
            
            <div className="flex items-center space-x-6 text-gray-600 mb-6">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                <span>{blogPost.author}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                <span>{blogPost.readTime}</span>
              </div>
              <span>{blogPost.date}</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button className="bg-orange-600 hover:bg-orange-700">
                <Heart className="w-4 h-4 mr-2" />
                Save Article
              </Button>
              <Button variant="outline" className="border-orange-200 text-orange-600">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card>
              <CardContent className="p-8">
                <div className="prose prose-lg max-w-none">
                  {blogPost.content.split('\n').map((paragraph, index) => {
                    if (paragraph.startsWith('## ')) {
                      return (
                        <h2 key={index} className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                          {paragraph.replace('## ', '')}
                        </h2>
                      );
                    } else if (paragraph.startsWith('### ')) {
                      return (
                        <h3 key={index} className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                          {paragraph.replace('### ', '')}
                        </h3>
                      );
                    } else if (paragraph.startsWith('- **') || paragraph.startsWith('1. **')) {
                      return (
                        <div key={index} className="mb-2">
                          <p className="text-gray-700 leading-relaxed">{paragraph}</p>
                        </div>
                      );
                    } else if (paragraph.trim() !== '') {
                      return (
                        <p key={index} className="text-gray-700 leading-relaxed mb-4">
                          {paragraph}
                        </p>
                      );
                    }
                    return null;
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-6">
              {/* Quick Contact */}
              <Card className="border-orange-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Need Medical Help?</h3>
                  <div className="space-y-3">
                    <a 
                      href="https://wa.me/917337541113?text=Need medical consultation"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="w-full bg-green-500 hover:bg-green-600">
                        <Phone className="w-4 h-4 mr-2" />
                        WhatsApp Consultation
                      </Button>
                    </a>
                    <Link to="/doctors">
                      <Button variant="outline" className="w-full border-orange-200 text-orange-600">
                        <BookOpen className="w-4 h-4 mr-2" />
                        Find Doctors
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              {/* Location Coverage */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <MapPin className="w-4 h-4 mr-2" />
                    Service Areas
                  </h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div>
                      <strong>Andhra Pradesh:</strong> Visakhapatnam, Vijayawada, Guntur, Tirupati
                    </div>
                    <div>
                      <strong>Telangana:</strong> Hyderabad, Warangal, Nizamabad, Karimnagar
                    </div>
                    <div>
                      <strong>Pan-India:</strong> 500+ cities covered
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tags */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Related Topics</h3>
                  <div className="flex flex-wrap gap-2">
                    {blogPost.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <Card className="mt-8 bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Start Your Healthcare Journey?
            </h3>
            <p className="text-gray-600 mb-6">
              Join thousands of satisfied patients across India who trust GEETH365 for their healthcare needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth">
                <Button size="lg" className="bg-orange-600 hover:bg-orange-700">
                  Sign Up Free
                </Button>
              </Link>
              <Link to="/doctors">
                <Button size="lg" variant="outline" className="border-orange-200 text-orange-600">
                  Find Doctors Now
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BlogPost;